import type { TFunction } from 'i18next';
import { contactLegalName } from '@/utils/contactDisplay';
import { personDisplayName } from '@/features/userManagement/userManagementUtils';
import type { Call, CallUserReference } from '@/types/calls';
import { formatAppDateTime } from '@/utils/dateUtils';
import {
  formatSecondsAdaptiveClock,
  formatSecondsHumanized,
} from '@/utils/durationFormat';
import { getCallDurationSeconds } from '@/utils/callHistoryDurationUtils';

export function isOfficerTerminatedCall(
  call: Pick<Call, 'terminated_at' | 'terminated_by'>
): boolean {
  return call.terminated_at !== null && call.terminated_at !== undefined;
}

export function getCallHistoryStatusLabelKey(
  call: Pick<Call, 'terminated_at' | 'terminated_by' | 'hangup_cause'>
): string {
  if (isOfficerTerminatedCall(call)) {
    return 'callHistory.statusTerminated';
  }
  return getHangupStatusLabelKey(call.hangup_cause);
}

export function getCallHistoryStatusPillVariant(
  call: Pick<Call, 'terminated_at' | 'terminated_by' | 'hangup_cause'>
): 'completed' | 'ended' | 'recording' | 'unknown' | 'terminated' {
  if (isOfficerTerminatedCall(call)) {
    return 'terminated';
  }
  return getStatusPillVariant(call.hangup_cause);
}

export function getHangupStatusLabelKey(hangupCause: string | undefined): string {
  if (!hangupCause) {
    return 'callHistory.statusUnknown';
  }
  switch (hangupCause) {
    case 'NORMAL_CLEARING':
      return 'callHistory.statusCompleted';
    case 'NO_ANSWER':
      return 'callHistory.statusNoAnswer';
    case 'USER_BUSY':
      return 'callHistory.statusBusy';
    case 'CALL_REJECTED':
      return 'callHistory.statusRejected';
    default:
      return 'callHistory.statusUnknown';
  }
}

export function getCallPartiesLabel(call: Call, t: TFunction): string {
  const unknown = t('common.unknown');
  const caller =
    personDisplayName(call.caller_user, '') ||
    call.caller_display ||
    call.caller_id_name ||
    call.caller_id_number ||
    unknown;
  const destination = call.contact
    ? contactLegalName(call.contact)
    : personDisplayName(call.callee_user, '') ||
      call.destination_display ||
      call.destination_number ||
      unknown;
  return t('callHistory.partyToParty', { from: caller, to: destination });
}

export function getCallPartyHref(user: CallUserReference): string {
  return user.is_nominal ? `/nominals/${user.id}` : `/users/${user.id}`;
}

export function getCallHistoryCallerLabel(call: Call, t: TFunction): string {
  return (
    personDisplayName(call.caller_user, '') ||
    call.caller_display ||
    call.caller_id_name ||
    call.caller_id_number ||
    t('common.unknown')
  );
}

export function getCallHistoryDestinationLabel(
  call: Call,
  t: TFunction
): string {
  if (call.contact) {
    return contactLegalName(call.contact);
  }
  return (
    personDisplayName(call.callee_user, '') ||
    call.destination_display ||
    call.destination_number ||
    t('common.unknown')
  );
}

export function nominalIsCaller(call: Call): boolean {
  if (!call.nominal?.id) {
    return false;
  }
  if (call.caller_user?.id) {
    return call.caller_user.id === call.nominal.id;
  }
  return call.callee_user?.id !== call.nominal.id;
}

export function getDashboardCallPartiesLabel(
  call: Call,
  showNominalData: boolean,
  t: TFunction
): string {
  if (showNominalData && nominalIsCaller(call) && call.nominal) {
    const destination = call.contact
      ? contactLegalName(call.contact)
      : call.destination_display ||
        call.destination_number ||
        t('common.unknown');
    return t('callHistory.partyToParty', {
      from: personDisplayName(call.nominal, call.nominal.username),
      to: destination,
    });
  }
  return getCallPartiesLabel(call, t);
}

export function getCallTypeLabel(
  call: Call,
  showNominalData: boolean,
  t: TFunction
): string {
  const fromNominal = showNominalData && nominalIsCaller(call);
  const toNominal =
    showNominalData &&
    Boolean(call.nominal?.id) &&
    !fromNominal &&
    call.callee_user?.id === call.nominal?.id;
  const fromLabel = fromNominal ? t('common.nominal') : t('listFilters.staff');
  const toLabel = call.contact
    ? t('common.contact')
    : toNominal
      ? t('common.nominal')
      : t('listFilters.staff');
  return t('callHistory.partyToParty', { from: fromLabel, to: toLabel });
}

export function getCallTypeDisplayLabel(
  call: Call,
  showNominalData: boolean,
  t: TFunction
): string {
  return t('dashboard.callTypePrefix', {
    type: getCallTypeLabel(call, showNominalData, t),
  });
}

export function getStatusPillVariant(
  hangupCause: string | undefined
): 'completed' | 'ended' | 'recording' | 'unknown' {
  switch (hangupCause) {
    case 'NORMAL_CLEARING':
      return 'completed';
    case 'NO_ANSWER':
    case 'USER_BUSY':
      return 'ended';
    case 'CALL_REJECTED':
      return 'recording';
    default:
      return 'unknown';
  }
}

export function formatDetailCallDurationDisplay(
  call: {
    duration?: number | string | null;
    call_duration?: string | null;
  },
  t: TFunction
): string {
  if (
    call.call_duration &&
    call.call_duration !== '0:00:00' &&
    call.call_duration !== '0:00'
  ) {
    return call.call_duration;
  }
  const durationSeconds = getCallDurationSeconds(call);
  if (durationSeconds > 0) {
    return formatSecondsAdaptiveClock(durationSeconds);
  }
  return t('common.unknown');
}

export function formatCallHistoryDateDuration(
  call: Call,
  t: TFunction,
  locale: string
): string {
  const date = call.start_stamp
    ? formatAppDateTime(call.start_stamp, locale)
    : '—';
  const duration = formatSecondsHumanized(getCallDurationSeconds(call));
  return t('dashboard.callDateDuration', { date, duration });
}
